import { Component } from '@theme/component';

/**
 * Countdown timer for an announcement bar entry.
 *
 * Reads a target date from the `data-date` attribute (YYYY-MM-DD) and renders
 * the remaining time until the end of that day into its `display` ref, using
 * the same unpadded `d:h:m:s` format as the live Dawn bar. When the timer
 * expires it stops and shows the text from `data-expired-text`.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [display]
 *
 * @extends {Component<Refs>}
 */
export class NaAnnouncementCountdown extends Component {
    /** @type {number|undefined} */
    #interval = undefined;

    connectedCallback() {
        super.connectedCallback();

        if (this.#targetTime === null) return;

        this.#render();
        this.#interval = setInterval(this.#render, 1000);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#stop();
    }

    #stop() {
        if (this.#interval === undefined) return;

        clearInterval(this.#interval);
        this.#interval = undefined;
    }

    /**
     * Target timestamp in milliseconds, or null when the date is missing/invalid.
     * @returns {number|null}
     */
    get #targetTime() {
        const date = this.dataset.date;

        if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;

        const parsed = new Date(`${date}T23:59:59`).getTime();

        return Number.isNaN(parsed) ? null : parsed;
    }

    #render = () => {
        const target = this.#targetTime;

        if (target === null) return;

        // The display ref may not exist yet while the theme editor is morphing
        // the section — skip this tick and let the next one render.
        const { display } = this.refs;
        if (!(display instanceof HTMLElement)) return;

        const remaining = target - Date.now();

        if (remaining <= 0) {
            this.#stop();
            display.textContent = this.dataset.expiredText || 'Expired';
            return;
        }

        const totalSeconds = Math.floor(remaining / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const segments = days > 0 ? [days, hours, minutes, seconds] : [hours, minutes, seconds];

        display.textContent = segments.join(':');
    };
}

if (!customElements.get('na-announcement-countdown-component')) {
    customElements.define('na-announcement-countdown-component', NaAnnouncementCountdown);
}
